import { format, formatDistanceToNow, parse } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Bot,
  CheckCircle2,
  Clock,
  DollarSign,
  History,
  Loader2,
  Shield,
  ShieldCheck,
  ShieldX,
  Sparkles,
  TrendingDown,
  TrendingUp,
  XCircle,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { Tabs } from '../components/Tabs';
import {
  type AgentCustomInstructionAuditEntry,
  type AgentLogEntry,
  type AgentMetrics,
  type AgentPermissionEntry,
  type PaginatedResponse,
  useAgentCustomInstructions,
  useAgentLogs,
  useAgentMetrics,
  useAgentPermissions,
  useOrganizationDetail,
  useUpdateAgentPermissions,
} from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(225, 20%, 16%)',
  border: '1px solid hsl(225, 15%, 25%)',
  borderRadius: '8px',
  color: 'hsl(0, 0%, 95%)',
};

const GRID_STROKE = 'hsl(225, 15%, 18%)';
const AXIS_STROKE = 'hsl(0, 0%, 45%)';
const SUCCESS_COLOR = 'hsl(142, 71%, 45%)';
const DANGER_COLOR = 'hsl(0, 84%, 60%)';
const WARNING_COLOR = 'hsl(38, 92%, 50%)';
const INFO_COLOR = 'hsl(217, 91%, 60%)';

const STATUS_CONFIG: Record<
  string,
  {
    color: string;
    icon: typeof CheckCircle2;
    variant: 'success' | 'danger' | 'warning' | 'info' | 'default';
  }
> = {
  completed: { color: SUCCESS_COLOR, icon: CheckCircle2, variant: 'success' },
  failed: { color: DANGER_COLOR, icon: XCircle, variant: 'danger' },
  running: { color: INFO_COLOR, icon: Loader2, variant: 'info' },
  queued: { color: WARNING_COLOR, icon: Clock, variant: 'warning' },
  cancelled: { color: AXIS_STROKE, icon: XCircle, variant: 'default' },
};

const AGENT_LABELS: Record<string, string> = {
  stage_prospect_intake: 'Prospect Intake',
  stage_first_contact: 'First Contact',
  stage_qualification_deep: 'Deep Qualification',
  stage_proposal_prep: 'Proposal Prep',
  stage_won_onboarding: 'Won Onboarding',
  stage_lost_analysis: 'Lost Analysis',
  lead_qualification: 'Lead Qualification',
  lead_enrichment: 'Lead Enrichment',
  follow_up_scheduler: 'Follow-up Scheduler',
  sentiment_analysis: 'Sentiment Analysis',
};

const AGENT_DESCRIPTIONS: Record<string, string> = {
  stage_prospect_intake: 'Evaluates new prospect data completeness and pipeline readiness',
  stage_first_contact: 'Crafts personalized outreach based on prospect profile',
  stage_qualification_deep: 'Deep assessment of fit, budget, and decision-making authority',
  stage_proposal_prep: 'Generates proposal talking points and competitive positioning',
  stage_won_onboarding: 'Creates onboarding checklists and success milestones',
  stage_lost_analysis: 'Analyzes loss reasons and recommends win-back strategies',
  lead_qualification: 'Scores and qualifies leads based on engagement signals',
  lead_enrichment: 'Enriches lead profiles with additional data points',
  follow_up_scheduler: 'Optimizes follow-up timing and messaging cadence',
  sentiment_analysis: 'Analyzes communication tone and engagement sentiment',
};

function formatAgentType(type: string) {
  return AGENT_LABELS[type] ?? type.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDuration(ms: number | null) {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

const DAYS_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

const STATUS_OPTIONS = ['completed', 'failed', 'running', 'queued', 'cancelled'];

const CATEGORY_LABELS: Record<string, string> = {
  leads: 'Lead Management',
  data: 'Data & Notes',
  system: 'System Access',
  communication: 'Communication',
};

function PermissionToggle({
  permission,
  onToggle,
  saving,
}: {
  permission: AgentPermissionEntry;
  onToggle: (key: string, granted: boolean) => void;
  saving: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-4 px-4 py-3 rounded-lg border transition-colors ${
        permission.isGranted
          ? 'border-success/20 bg-success/5'
          : 'border-border bg-surface-hover/30'
      }`}
    >
      <div className="flex-shrink-0">
        {permission.isGranted ? (
          <ShieldCheck size={18} className="text-success" />
        ) : (
          <ShieldX size={18} className="text-text-muted" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-text">{permission.name}</p>
          {permission.hasOverride && <Badge variant="warning">Override</Badge>}
          {!permission.hasOverride && permission.isGranted && (
            <Badge variant="default">Default</Badge>
          )}
        </div>
        <p className="text-xs text-text-muted mt-0.5">{permission.description}</p>
        {permission.tools.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {permission.tools.map((tool) => (
              <span
                key={tool}
                className="px-1.5 py-0.5 text-[10px] bg-surface border border-border rounded text-text-muted font-mono"
              >
                {tool}
              </span>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={permission.isGranted}
        disabled={saving}
        onClick={() => onToggle(permission.key, !permission.isGranted)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
          permission.isGranted ? 'bg-success' : 'bg-border'
        } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
            permission.isGranted ? 'translate-x-[18px]' : 'translate-x-[3px]'
          }`}
        />
      </button>
    </div>
  );
}

function PermissionsPanel({
  organizationId,
  agentType,
}: {
  organizationId: string;
  agentType: string;
}) {
  const { data, isLoading } = useAgentPermissions(organizationId, agentType);
  const updateMutation = useUpdateAgentPermissions();

  function handleToggle(permissionKey: string, isGranted: boolean) {
    updateMutation.mutate({
      orgId: organizationId,
      agentType,
      permissions: [{ permission: permissionKey, isGranted }],
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-16 bg-border rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!data) {
    return <div className="py-8 text-center text-text-muted">No permission data available</div>;
  }

  const grouped = data.permissions.reduce(
    (acc, perm) => {
      const cat = perm.category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(perm);
      return acc;
    },
    {} as Record<string, AgentPermissionEntry[]>,
  );

  const grantedCount = data.permissions.filter((p) => p.isGranted).length;
  const overrideCount = data.permissions.filter((p) => p.hasOverride).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-surface border border-border rounded-lg">
          <Shield size={14} className="text-primary" />
          <span className="text-sm text-text">
            {grantedCount}/{data.permissions.length} granted
          </span>
        </div>
        {overrideCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-warning/10 border border-warning/30 rounded-lg">
            <span className="text-sm text-warning">{overrideCount} overrides</span>
          </div>
        )}
      </div>

      {Object.entries(grouped).map(([category, permissions]) => (
        <div key={category}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">
            {CATEGORY_LABELS[category] ?? category}
          </h3>
          <div className="space-y-2">
            {permissions.map((perm) => (
              <PermissionToggle
                key={perm.key}
                permission={perm}
                onToggle={handleToggle}
                saving={updateMutation.isPending}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const SOURCE_LABELS: Record<AgentCustomInstructionAuditEntry['source'], string> = {
  manual: 'Manual edit',
  ai_generated: 'AI generated',
  ai_enriched: 'AI enriched',
};

const SOURCE_VARIANT: Record<
  AgentCustomInstructionAuditEntry['source'],
  'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'
> = {
  manual: 'default',
  ai_generated: 'info',
  ai_enriched: 'primary',
};

function AuditEntryRow({ entry }: { entry: AgentCustomInstructionAuditEntry }) {
  return (
    <div className="px-4 py-3 border-b border-border last:border-b-0">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text">{entry.actorName}</span>
          <Badge variant={SOURCE_VARIANT[entry.source]}>{SOURCE_LABELS[entry.source]}</Badge>
        </div>
        <span className="text-xs text-text-muted">
          {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div>
          <p className="text-text-muted mb-1">Before</p>
          <p className="text-text-secondary whitespace-pre-wrap">
            {entry.beforeText ?? '(none)'}
          </p>
        </div>
        <div>
          <p className="text-text-muted mb-1">After</p>
          <p className="text-text-secondary whitespace-pre-wrap">{entry.afterText}</p>
        </div>
      </div>
    </div>
  );
}

function CustomInstructionsPanel({
  organizationId,
  agentType,
}: {
  organizationId: string;
  agentType: string;
}) {
  const { data, isLoading } = useAgentCustomInstructions(organizationId, agentType);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 bg-border rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-surface rounded-xl border border-border p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-primary" />
          <h3 className="text-sm font-medium text-text-secondary">Current Custom Instruction</h3>
        </div>
        {data?.current ? (
          <p className="text-sm text-text whitespace-pre-wrap">{data.current}</p>
        ) : (
          <p className="text-sm text-text-muted">No custom instruction set for this agent yet.</p>
        )}
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <History size={16} className="text-primary" />
          <h2 className="text-sm font-medium text-text-secondary">Audit History</h2>
        </div>
        {data?.history && data.history.length > 0 ? (
          <div>
            {data.history.map((entry) => (
              <AuditEntryRow key={entry.id} entry={entry} />
            ))}
          </div>
        ) : (
          <div className="px-5 py-8 text-center text-text-muted text-sm">No audit history yet.</div>
        )}
      </div>
    </div>
  );
}

export function AgentDetail({
  organizationId,
  agentType,
}: {
  organizationId: string;
  agentType: string;
}) {
  const [days, setDays] = useState(30);
  const [logsPage, setLogsPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: orgData, isLoading: orgLoading } = useOrganizationDetail(organizationId);
  const { data: metrics, isLoading: metricsLoading } = useAgentMetrics({
    days,
    organizationId,
    agentType,
  });
  const { data: logs, isLoading: logsLoading } = useAgentLogs({
    page: logsPage,
    limit: 20,
    organizationId,
    agentType,
    ...(statusFilter && { status: statusFilter }),
    ...(searchQuery && { search: searchQuery }),
  });

  const summary = metrics?.summary;

  const dailyChartData =
    metrics?.dailyExecutions.map((d) => ({
      date: format(parse(d.date, 'yyyy-MM-dd', new Date()), 'MMM d'),
      completed: d.completed,
      failed: d.failed,
    })) ?? [];

  const statusPieData = summary
    ? [
        { name: 'Completed', value: summary.completed, color: SUCCESS_COLOR },
        { name: 'Failed', value: summary.failed, color: DANGER_COLOR },
        { name: 'Running', value: summary.running, color: INFO_COLOR },
        { name: 'Queued', value: summary.queued, color: WARNING_COLOR },
        { name: 'Cancelled', value: summary.cancelled, color: AXIS_STROKE },
      ].filter((s) => s.value > 0)
    : [];

  const totalPages = logs ? Math.ceil(logs.total / 20) : 0;

  if (orgLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-border rounded w-48 animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <StatCard key={i} label="" value="" icon={Zap} loading />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/pipeline-agents')}
        className="flex items-center gap-2 text-sm text-text-secondary hover:text-text transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Pipeline Agents
      </button>

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Bot size={24} className="text-primary" />
            <h1 className="text-2xl font-semibold">{formatAgentType(agentType)}</h1>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="primary">{orgData?.name ?? 'Organization'}</Badge>
            {AGENT_DESCRIPTIONS[agentType] && (
              <span className="text-sm text-text-muted">{AGENT_DESCRIPTIONS[agentType]}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {summary && summary.running > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-info/10 border border-info/30 rounded-lg">
              <Loader2 size={14} className="text-info animate-spin" />
              <span className="text-sm text-info font-medium">{summary.running} running</span>
            </div>
          )}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
          >
            {DAYS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Total Executions"
          value={summary?.totalExecutions ?? 0}
          icon={Zap}
          loading={metricsLoading}
        />
        <StatCard
          label="Success Rate"
          value={summary ? `${summary.successRate}%` : '0%'}
          icon={TrendingUp}
          loading={metricsLoading}
        />
        <StatCard
          label="Failure Rate"
          value={summary ? `${summary.failureRate}%` : '0%'}
          icon={TrendingDown}
          loading={metricsLoading}
        />
        <StatCard
          label="Avg Duration"
          value={formatDuration(summary?.avgDurationMs ?? 0)}
          icon={Clock}
          loading={metricsLoading}
        />
        <StatCard
          label="Total Cost"
          value={summary ? `$${parseFloat(summary.totalCostUsd).toFixed(2)}` : '$0.00'}
          icon={DollarSign}
          loading={metricsLoading}
        />
      </div>

      <div className="bg-surface rounded-xl border border-border p-5">
        <Tabs
          defaultTab="monitoring"
          tabs={[
            {
              key: 'monitoring',
              label: 'Monitoring',
              content: (
                <MonitoringContent
                  summary={summary}
                  metricsLoading={metricsLoading}
                  metrics={metrics}
                  dailyChartData={dailyChartData}
                  statusPieData={statusPieData}
                  logs={logs}
                  logsLoading={logsLoading}
                  logsPage={logsPage}
                  totalPages={totalPages}
                  statusFilter={statusFilter}
                  searchQuery={searchQuery}
                  setLogsPage={setLogsPage}
                  setStatusFilter={setStatusFilter}
                  setSearchQuery={setSearchQuery}
                />
              ),
            },
            {
              key: 'permissions',
              label: 'Permissions',
              content: <PermissionsPanel organizationId={organizationId} agentType={agentType} />,
            },
            {
              key: 'custom-instructions',
              label: 'Custom Instructions',
              content: (
                <CustomInstructionsPanel organizationId={organizationId} agentType={agentType} />
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}

function MonitoringContent({
  summary,
  metricsLoading,
  metrics,
  dailyChartData,
  statusPieData,
  logs,
  logsLoading,
  logsPage,
  totalPages,
  statusFilter,
  searchQuery,
  setLogsPage,
  setStatusFilter,
  setSearchQuery,
}: {
  summary: AgentMetrics['summary'] | undefined;
  metricsLoading: boolean;
  metrics: AgentMetrics | undefined;
  dailyChartData: Array<{ date: string; completed: number; failed: number }>;
  statusPieData: Array<{ name: string; value: number; color: string }>;
  logs: PaginatedResponse<AgentLogEntry> | undefined;
  logsLoading: boolean;
  logsPage: number;
  totalPages: number;
  statusFilter: string;
  searchQuery: string;
  setLogsPage: (v: number | ((p: number) => number)) => void;
  setStatusFilter: (v: string) => void;
  setSearchQuery: (v: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {(['completed', 'failed', 'running', 'queued', 'cancelled'] as const).map((status) => {
          const config = STATUS_CONFIG[status];
          const count = summary?.[status] ?? 0;
          const Icon = config.icon;
          return (
            <div
              key={status}
              className="flex items-center gap-3 bg-surface rounded-lg border border-border p-3"
            >
              <Icon
                size={18}
                style={{ color: config.color }}
                className={status === 'running' ? 'animate-spin' : ''}
              />
              <div>
                <div className="text-lg font-semibold text-text">
                  {metricsLoading ? '-' : count}
                </div>
                <div className="text-xs text-text-muted capitalize">{status}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Execution Trends</h2>
          {metricsLoading ? (
            <div className="h-64 flex items-center justify-center text-text-muted">Loading...</div>
          ) : dailyChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyChartData}>
                <defs>
                  <linearGradient id="agentCompletedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SUCCESS_COLOR} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={SUCCESS_COLOR} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="agentFailedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={DANGER_COLOR} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={DANGER_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="date" stroke={AXIS_STROKE} fontSize={12} />
                <YAxis stroke={AXIS_STROKE} fontSize={12} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="completed"
                  stroke={SUCCESS_COLOR}
                  strokeWidth={2}
                  fill="url(#agentCompletedGrad)"
                  name="Completed"
                />
                <Area
                  type="monotone"
                  dataKey="failed"
                  stroke={DANGER_COLOR}
                  strokeWidth={2}
                  fill="url(#agentFailedGrad)"
                  name="Failed"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">
              No execution data for this period
            </div>
          )}
        </div>

        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Status Distribution</h2>
          {metricsLoading ? (
            <div className="h-64 flex items-center justify-center text-text-muted">Loading...</div>
          ) : statusPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  dataKey="value"
                  nameKey="name"
                  paddingAngle={2}
                  label={({ name, value }) => `${name} (${value})`}
                >
                  {statusPieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <AlertTriangle size={16} className="text-danger" />
          <h2 className="text-sm font-medium text-text-secondary">Recent Failures</h2>
        </div>
        <div className="divide-y divide-border max-h-[240px] overflow-y-auto">
          {metricsLoading ? (
            ['s1', 's2', 's3'].map((sk) => (
              <div key={sk} className="px-5 py-3 animate-pulse">
                <div className="h-4 bg-border rounded w-48 mb-2" />
                <div className="h-3 bg-border rounded w-32" />
              </div>
            ))
          ) : metrics?.recentFailures?.length ? (
            metrics.recentFailures.map((failure) => (
              <div key={failure.id} className="px-5 py-3 hover:bg-surface-hover transition-colors">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text">
                    {formatAgentType(failure.agentType)}
                  </span>
                  <span className="text-xs text-text-muted">
                    {formatDistanceToNow(new Date(failure.createdAt), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-xs text-danger truncate">
                  {failure.errorMessage ?? 'Unknown error'}
                </p>
              </div>
            ))
          ) : (
            <div className="px-5 py-8 text-center text-text-muted text-sm">No recent failures</div>
          )}
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-primary" />
              <h2 className="text-sm font-medium text-text-secondary">Execution Logs</h2>
              {logs && <span className="text-xs text-text-muted">({logs.total} total)</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setLogsPage(1);
              }}
              placeholder="Search by lead or error..."
              className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary w-64"
            />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setLogsPage(1);
              }}
              className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Lead</th>
                <th className="px-5 py-3 font-medium">Model</th>
                <th className="px-5 py-3 font-medium">Duration</th>
                <th className="px-5 py-3 font-medium">Tokens</th>
                <th className="px-5 py-3 font-medium">Cost</th>
                <th className="px-5 py-3 font-medium">Error</th>
                <th className="px-5 py-3 font-medium">Time</th>
              </tr>
            </thead>
            <tbody>
              {logsLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={i} className="border-b border-border animate-pulse">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-5 py-3">
                        <div className="h-4 bg-border rounded w-16" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : logs?.data?.length ? (
                logs.data.map((log) => {
                  const statusCfg = STATUS_CONFIG[log.status] ?? STATUS_CONFIG.cancelled;
                  const StatusIcon = statusCfg.icon;
                  return (
                    <tr
                      key={log.id}
                      className="border-b border-border hover:bg-surface-hover transition-colors"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5">
                          <StatusIcon
                            size={14}
                            style={{ color: statusCfg.color }}
                            className={log.status === 'running' ? 'animate-spin' : ''}
                          />
                          <Badge variant={statusCfg.variant}>{log.status}</Badge>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-text-secondary truncate max-w-[140px]">
                        {log.leadName ?? '-'}
                      </td>
                      <td className="px-5 py-3 text-text-muted text-xs">{log.modelUsed ?? '-'}</td>
                      <td className="px-5 py-3 text-text-secondary">
                        {formatDuration(log.durationMs)}
                      </td>
                      <td className="px-5 py-3 text-text-secondary text-xs">
                        {log.inputTokens + log.outputTokens > 0
                          ? `${log.inputTokens.toLocaleString()} / ${log.outputTokens.toLocaleString()}`
                          : '-'}
                      </td>
                      <td className="px-5 py-3 text-text-secondary">
                        {parseFloat(log.costUsd) > 0
                          ? `$${parseFloat(log.costUsd).toFixed(4)}`
                          : '-'}
                      </td>
                      <td className="px-5 py-3 text-danger text-xs truncate max-w-[200px]">
                        {log.errorMessage ?? '-'}
                      </td>
                      <td className="px-5 py-3 text-text-muted text-xs whitespace-nowrap">
                        {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-text-muted">
                    No executions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            <span className="text-sm text-text-muted">
              Page {logsPage} of {totalPages} ({logs?.total} results)
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                disabled={logsPage === 1}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setLogsPage((p) => Math.min(totalPages, p + 1))}
                disabled={logsPage >= totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
